import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudinaryImage } from '@cloudinary/url-gen';
import { FullscreenImageProps } from './types';
import { Definition } from '../../../display/Definition';
import {
  StyledAdvancedImage,
  InfoContainer,
  StyledTypography,
  ButtonStyled,
} from './styles';
import ModalContainer from '../../containers/ModalContainer';
import {
  buildCldImage,
  buildCloudinaryDownloadUrl,
  CloudinaryQuality,
} from '../../../../utils/cloudinary';
import { fetchDownloadBlob } from '../../../../utils/imageDownload';
import { CloudinaryQualitySelector } from '../../../inputs/CloudinaryQualitySelector';
import { saveBlob } from '../../../../utils/saveBlob';
import { PhotoLocationPanel } from './PhotoLocationPanel';

// Migración Hetzner: el display vuelve a <AdvancedImage> construido con el SDK
// vía buildCldImage (soporta Hetzner publicUrl + legacy publicId). El handler
// de descarga usa `currentImage` (string ya resuelto). Fallback legacy a
// reconstrucción por publicId solo si no hay URL ni objeto image.

export const ViewFullscreenImage = ({
  onClickClose,
  currentImage,
  image,
  title,
  currentDate,
  currentHours,
  currentMinutes,
  roid,
  publicId,
}: FullscreenImageProps) => {
  const { t } = useTranslation();
  const [quality, setQuality] = useState<CloudinaryQuality>(
    CloudinaryQuality.Optimized
  );

  const cldImg: CloudinaryImage | null = useMemo(
    () => buildCldImage(image),
    [image]
  );

  const handleDownload = async () => {
    try {
      // La URL la decide `resolveDownloadUrl` (la «alta» es el ORIGINAL, con
      // su GPS); si falla, `fetchDownloadBlob` cae al original y después al
      // camino legacy por publicId o a la URL ya resuelta `currentImage`.
      const blob = await fetchDownloadBlob(image, quality, [
        publicId ? buildCloudinaryDownloadUrl(publicId, quality) : null,
        currentImage,
      ]);

      // `title` ya trae la extensión.
      await saveBlob(blob, title);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <ModalContainer fill="black" onClickClose={onClickClose}>
      {cldImg ? (
        <StyledAdvancedImage cldImg={cldImg} />
      ) : (
        <StyledTypography size="S">{title}</StyledTypography>
      )}
      <InfoContainer>
        <StyledTypography size="S">{title}</StyledTypography>
        <Definition title={t('common:creationDate')} value={currentDate} />
        <Definition
          title={t('common:creationTime')}
          value={`${currentHours}:${currentMinutes}`}
        />
        <Definition title="ROID" value={roid} />
        {/* Dónde se hizo, leído del EXIF del original (spec fotos
            geolocalizadas § 16). Solo con el flag; sin él no existe. */}
        <PhotoLocationPanel image={image} />
        <CloudinaryQualitySelector value={quality} onChange={setQuality} />
        <ButtonStyled
          variant="primary"
          text={t('buttons:download')}
          onClick={handleDownload}
        />
      </InfoContainer>
    </ModalContainer>
  );
};
